import { plural } from "@lingui/core/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

import { apiPutPhysiciansUuid } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { ErrorPage } from "../../../app/error-page";
import { useCountries } from "../../../catalogs/countries";
import { Button } from "../../../components/ui-kit/button";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { hasMonitoringAccess } from "../../../constants/permissions";
import { assert } from "../../../utils/utils";
import { PhysiciansModal } from "../../admin/common/physicians-modal";
import { loadPhysicians } from "../../admin/physicians/physicians";
import { PatientSubpage } from "../common/patient-subpage";
import { PatientPhysicians } from "./patient-physicians";

export const PatientPhysiciansRoute = observer(
  function PatientPhysiciansRoute(props: { app: AppModel }) {
    const { app } = props;
    const patient = app.requirePatient();
    assert(patient.data, "Patient data must be loaded to view physicians");
    const countries = useCountries();

    const canAccess = hasMonitoringAccess(patient.api.permissions);
    const [editing, setEditing] = useState(false);
    const [model] = useState(
      () =>
        new PatientPhysicians(
          [],
          patient.api.permissions,
          patient.api.requireUserUUID(),
        ),
    );

    useEffect(() => {
      if (canAccess && !app.adminShit) {
        void app.initAdmin();
      }
    }, [app, canAccess]);

    const physicians = useMemo(
      () => patient.data?.physicians ?? [],
      [patient.data.physicians],
    );
    const currentUuids = physicians.map((p) => p.uuid);
    const canEdit = app.adminShit?.initialized ?? false;

    useEffect(() => {
      model.updateData(physicians);
    }, [model, physicians]);

    if (!canAccess) {
      return (
        <ErrorPage
          title="Access denied"
          message="You don't have permission to view physicians for this patient."
        />
      );
    }

    return (
      <PatientSubpage title="Physicians">
        <DataTable
          model={model}
          itemKeyField="uuid"
          // Standalone `plural` (global i18n) rather than `useLingui` — the
          // rest of this component is still unwrapped, so there is no `t` to
          // reach for. Locale changes reload the page, so nothing here needs
          // to be reactive.
          filterNoun={(count) =>
            plural(count, {
              one: "physician assigned",
              other: "physicians assigned",
            })
          }
          filterActions={
            <Button
              disabled={!canEdit}
              onClick={() => setEditing(true)}
              title={canEdit ? undefined : "Loading admin data, please wait…"}
            >
              Edit physicians
            </Button>
          }
          columns={[
            {
              header: "Family name",
              attr: "family_name",
              type: "string",
            },
            {
              header: "Given name",
              attr: "given_name",
              type: "string",
            },
            {
              header: "Country",
              attr: "country",
              type: "enum",
              values: countries.countriesEnum,
            },
            {
              header: "Region",
              cell: ({ item }) =>
                item.country && item.region != null
                  ? (countries.getRegionSchema(item.country, item.region)
                      ?.region_name ?? item.region)
                  : "—",
            },
            {
              header: "Email",
              attr: "email",
              type: "string",
            },
            {
              header: "Phone",
              cell: ({ item }) => item.phone?.[0] ?? "—",
            },
            {
              header: "Clinics",
              cell: ({ item }) => item.clinics?.length ?? 0,
            },
          ]}
        />

        {editing && canEdit && (
          <PatientPhysiciansEditModal
            app={app}
            patientName={patient.data.full_name ?? `#${patient.patientId}`}
            patientCountry={patient.data.country}
            currentUuids={currentUuids}
            onClose={() => setEditing(false)}
          />
        )}
      </PatientSubpage>
    );
  },
);

const PatientPhysiciansEditModal = observer(
  function PatientPhysiciansEditModal(props: {
    app: AppModel;
    patientName: string;
    patientCountry: string | null | undefined;
    currentUuids: string[];
    onClose: () => void;
  }) {
    const { app, patientName, patientCountry, currentUuids, onClose } = props;
    const [model] = useState(() => app.requireAdminShit().createPhysicians());
    const { alert } = useDialog();

    const handleConfirm = async ({ selected }: { selected: any[] }) => {
      const patient = app.requirePatient();
      const selectedSet = new Set<string>(selected as string[]);
      const currentSet = new Set(currentUuids);
      const toAppend = (selected as string[]).filter(
        (uuid) => !currentSet.has(uuid),
      );
      const toRemove = currentUuids.filter((uuid) => !selectedSet.has(uuid));
      if (toAppend.length === 0 && toRemove.length === 0) {
        return;
      }
      const patientId = patient.patientId;
      const requests = [
        ...toAppend.map((uuid) => ({
          uuid,
          body: { append_patients: [patientId] },
        })),
        ...toRemove.map((uuid) => ({
          uuid,
          body: { delete_patients: [patientId] },
        })),
      ];
      const results = await Promise.allSettled(
        requests.map(({ uuid, body }) =>
          apiPutPhysiciansUuid({ path: { uuid }, body }),
        ),
      );
      // Always refetch so the UI reflects whatever did succeed,
      // even if some PUTs failed.
      try {
        await patient.fetch();
      } catch {
        // surfaced below if there are also request failures; otherwise ignore
      }
      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        await alert(
          `${failures} of ${requests.length} physician update${requests.length === 1 ? "" : "s"} failed. The list has been refreshed to reflect the current state.`,
          "Failed to update physicians",
        );
      }
    };

    return (
      <PhysiciansModal
        title={`Edit physicians for patient: ${patientName}`}
        model={model}
        mode="multi"
        preselected={currentUuids}
        filter={patientCountry ? { country: [patientCountry] } : undefined}
        allowCreate
        app={app}
        onAfterCreate={() => loadPhysicians(model)}
        onClose={onClose}
        onConfirm={handleConfirm}
      />
    );
  },
);
